import { Field, Input, Select, Textarea, clasesDeControl } from '@/ui/Field';
import { cn } from '@/lib/cn';
import {
  estadoDeRango, fechaDesplazada, reglaDeDuracion,
  type CampoDeFormulario, type Periodo, type ValorDeCampo,
} from './api';

/**
 * Un campo del formulario dinámico.
 *
 * **Nada de esto está escrito a mano en el cliente.** El tipo de campo, sus
 * opciones, sus límites y sus reglas de fecha los define quien configura el
 * tipo de solicitud, y llegan en `GET /solicitudes/catalog`. Aquí solo se
 * dibujan.
 *
 * Los límites que se pintan (`min`, `max`, `minLength`, `maxLength`) son
 * comodidad del navegador: **quien valida es el servidor**, que repite todas
 * estas reglas. Sin eso, un `<input>` deshabilitado o acotado sería una defensa
 * de mentira — se sigue pudiendo mandar el PUT a mano.
 *
 * ## Lo que se gana con JSX
 *
 * Las opciones de `select`, `radio` y `checkbox` se interpolaban en HTML **sin
 * escapar** (`<option value="${o}">${o}</option>`). Las escribe quien configura
 * el tipo desde la pestaña «Tipos», y las lee toda la organización: una comilla
 * rompía el marcado y una etiqueta con `<img onerror=…>` se ejecutaba en la
 * pantalla de cada colaborador. `test_escapado.py` no lo veía porque `o` es una
 * variable de bucle, no un `objeto.propiedad` con nombre conocido. En JSX el
 * escape es la única opción disponible.
 */
export function CampoDinamico({
  campo, valor, error, onCambio, onArchivo, valorReferido,
}: {
  campo: CampoDeFormulario;
  valor: ValorDeCampo;
  error?: string;
  onCambio: (v: ValorDeCampo) => void;
  /** El File de verdad, para los campos de archivo. `valor` guarda solo el
   *  nombre: un File no se puede comparar ni serializar como el resto. */
  onArchivo?: (f: File | null) => void;
  /** El valor del campo al que este apunta con `date_after`, si lo hay. */
  valorReferido?: string;
}) {
  const id = `solf-${campo.key}`;

  // Un período no es UN control, son dos, y su etiqueta rotula al grupo. Un
  // <label for> ahí apuntaría a nada, así que va por su cuenta.
  if (campo.type === 'rango_fechas') {
    return (
      <RangoDeFechas
        campo={campo}
        valor={(valor && typeof valor === 'object' && !Array.isArray(valor) ? valor : null) as Periodo | null}
        error={error}
        onCambio={onCambio}
      />
    );
  }

  // Autollenado: se muestra lo que trae el servidor y se bloquea. El servidor lo
  // vuelve a poner al recibir, así que editarlo no serviría de nada.
  if (campo.autofill) {
    return (
      <Field label={campo.label} ayuda="Tomado de tu ficha" id={id}>
        <Input value={campo.valor_autollenado ?? ''} readOnly disabled />
      </Field>
    );
  }

  const comun = {
    label: campo.label,
    error,
    opcional: !campo.required,
    id,
    ayuda: campo.help || undefined,
  };
  const texto = typeof valor === 'string' ? valor : '';

  if (campo.type === 'textarea') {
    return (
      <Field {...comun}>
        <Textarea
          rows={3}
          value={texto}
          minLength={campo.min_length ?? undefined}
          maxLength={campo.max_length ?? undefined}
          onChange={(e) => onCambio(e.target.value)}
        />
      </Field>
    );
  }

  if (campo.type === 'select') {
    return (
      <Field {...comun}>
        <Select value={texto} onChange={(e) => onCambio(e.target.value)}>
          <option value="">— Selecciona —</option>
          {(campo.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      </Field>
    );
  }

  if (campo.type === 'radio' || campo.type === 'checkbox') {
    const multiple = campo.type === 'checkbox';
    const marcados = multiple
      ? (Array.isArray(valor) ? valor.map(String) : [])
      : (texto ? [texto] : []);
    const idAyuda = `${id}-ayuda`;
    // NO va dentro de `Field`, y la razón es concreta: en un grupo de opciones
    // ningún elemento lleva el `id` del campo —los controles comparten `name`,
    // no `id`—, así que el `<label htmlFor>` de `Field` apuntaba a la nada.
    // Pulsar la etiqueta no hacía nada, el lector de pantalla leía los controles
    // sin el nombre del grupo, y el foco de la validación tampoco encontraba
    // dónde aterrizar. Aquí el grupo se rotula con `aria-labelledby`, que es lo
    // que existe para esto, y la primera opción se queda el `id` para que el
    // foco tenga destino.
    return (
      <div
        role="group"
        aria-labelledby={`${id}-label`}
        aria-describedby={error || campo.help ? idAyuda : undefined}
        className="flex flex-col gap-1.5"
      >
        <p id={`${id}-label`} className="text-3 font-semibold text-ink">
          {campo.label}
          {!campo.required && (
            <span className="ml-1.5 text-2 font-normal text-muted">opcional</span>
          )}
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-2 pt-0.5">
          {(campo.options ?? []).map((o, i) => (
            <label key={o} className="inline-flex cursor-pointer items-center gap-2 text-3 text-ink">
              <input
                id={i === 0 ? id : undefined}
                type={multiple ? 'checkbox' : 'radio'}
                name={id}
                value={o}
                checked={marcados.includes(o)}
                onChange={(ev) => {
                  if (!multiple) { onCambio(o); return; }
                  const set = new Set(marcados);
                  if (ev.target.checked) set.add(o); else set.delete(o);
                  onCambio([...set]);
                }}
                className="size-4 shrink-0 accent-[var(--color-brand)]"
              />
              {o}
            </label>
          ))}
        </div>
        {(error || campo.help) && (
          <p
            id={idAyuda}
            className={cn('text-2 leading-snug', error ? 'text-danger-ink' : 'text-muted')}
            role={error ? 'alert' : undefined}
          >
            {error ?? campo.help}
          </p>
        )}
      </div>
    );
  }

  if (campo.type === 'file') {
    return (
      <Field
        {...comun}
        ayuda={campo.required
          ? 'Sin este soporte la solicitud no se puede aprobar.'
          : campo.help || undefined}
      >
        <input
          type="file"
          id={id}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            onArchivo?.(f);
            onCambio(f ? f.name : '');
          }}
          data-campo-archivo={campo.key}
          className="w-full rounded-xs border border-field-border bg-surface px-3 py-2 text-3 text-ink
                     file:mr-3 file:rounded-xs file:border-0 file:bg-sunken file:px-3 file:py-1.5
                     file:text-2 file:font-semibold file:text-ink-2
                     focus:border-brand-ink focus:outline-none focus:ring-2 focus:ring-accent/35"
        />
      </Field>
    );
  }

  if (campo.type === 'date') {
    // Los límites llegan como DESPLAZAMIENTOS en días respecto de hoy, no como
    // fechas. Y el `min` real gana el más restrictivo entre el suyo y el del
    // campo al que apunta con `date_after`.
    const base = campo.date_min != null && String(campo.date_min) !== ''
      ? fechaDesplazada(Number(campo.date_min)) : '';
    const min = valorReferido && valorReferido > base ? valorReferido : base;
    const max = campo.date_max != null && String(campo.date_max) !== ''
      ? fechaDesplazada(Number(campo.date_max)) : undefined;
    return (
      <Field {...comun}>
        <Input
          type="date"
          value={texto}
          min={min || undefined}
          max={max}
          onChange={(e) => onCambio(e.target.value)}
        />
      </Field>
    );
  }

  const tipoHtml = campo.type === 'number' ? 'number'
    : campo.type === 'email' ? 'email'
    : campo.type === 'phone' ? 'tel'
    : 'text';

  return (
    <Field {...comun}>
      <Input
        type={tipoHtml}
        value={texto}
        min={campo.min ?? undefined}
        max={campo.max ?? undefined}
        minLength={campo.min_length ?? undefined}
        maxLength={campo.max_length ?? undefined}
        placeholder={campo.type === 'email' ? 'nombre@empresa.com'
          : campo.type === 'phone' ? '300 123 4567' : undefined}
        onChange={(e) => onCambio(e.target.value)}
      />
    </Field>
  );
}

/**
 * El período (`rango_fechas`): un solo dato con dos extremos.
 *
 * Se presenta como una SECUENCIA numerada (1 → 2) porque la duda del usuario no
 * es «qué formato» sino «qué elijo primero y qué estoy pidiendo». El bloque de
 * duración vive aquí y no después de enviar, porque «¿cuántos días son?» se
 * pregunta justo ahora.
 *
 * Al mover el inicio, el calendario del «hasta» deja de ofrecer días
 * imposibles, pero **la fecha ya elegida no se borra**: borrarla en silencio
 * dejaba al usuario sin dato y sin explicación. Se conserva, y el bloque de
 * abajo dice por qué está mal.
 */
function RangoDeFechas({
  campo, valor, error, onCambio,
}: {
  campo: CampoDeFormulario;
  valor: Periodo | null;
  error?: string;
  onCambio: (v: ValorDeCampo) => void;
}) {
  const id = `solf-${campo.key}`;
  const inicio = valor?.inicio ?? '';
  const fin = valor?.fin ?? '';
  const minInicio = campo.date_min != null && String(campo.date_min) !== ''
    ? fechaDesplazada(Number(campo.date_min)) : undefined;
  const maxInicio = campo.date_max != null && String(campo.date_max) !== ''
    ? fechaDesplazada(Number(campo.date_max)) : undefined;

  const estado = estadoDeRango(campo, inicio || undefined, fin || undefined);
  const regla = reglaDeDuracion(campo);
  const icono = estado.estado === 'ok' ? 'bi-check-circle-fill'
    : estado.estado === 'error' ? 'bi-exclamation-triangle-fill'
    : 'bi-calendar-range';

  const poner = (parte: 'inicio' | 'fin', v: string) => {
    const nuevo = { inicio, fin, [parte]: v };
    // O va completo o va vacío: media fecha no significa nada y el servidor la
    // trataría como campo sin llenar.
    onCambio(nuevo.inicio || nuevo.fin ? nuevo : '');
  };

  return (
    <div className="flex flex-col gap-1.5" role="group" aria-labelledby={`${id}-label`}>
      <p id={`${id}-label`} className="text-3 font-semibold text-ink">
        {campo.label}
        {!campo.required && <span className="ml-1.5 text-2 font-normal text-muted">opcional</span>}
      </p>
      {/* La ayuda sube ENCIMA de las fechas: leerla después de haberlas elegido
          no sirve de nada. Si quien configura el tipo escribió la suya, manda
          la suya — añadir debajo la genérica sería decir dos veces lo mismo. */}
      <p className="text-2 leading-relaxed text-muted">
        {campo.help || 'Selecciona el primer y el último día del período que deseas solicitar.'}
      </p>

      <div className="mt-1 flex flex-wrap items-end gap-3 sm:flex-nowrap">
        <div className="min-w-40 flex-1">
          <label className="mb-1 flex items-center gap-1.5 text-2 font-semibold text-ink-2"
                 htmlFor={`${id}-inicio`}>
            <span aria-hidden="true"
                  className="inline-grid size-4 place-items-center rounded-full bg-sunken text-1 font-bold">1</span>
            Fecha de inicio
          </label>
          {/* Suelto: cada mitad lleva su propio `<label for>` y el grupo su
              `aria-labelledby`. Un `Field` aquí duplicaría la etiqueta. */}
          <input
            type="date" id={`${id}-inicio`} value={inicio}
            min={minInicio} max={maxInicio}
            aria-describedby={`${id}-info`}
            className={clasesDeControl}
            onChange={(e) => poner('inicio', e.target.value)}
          />
          <span className="mt-1 block text-1 text-muted">Primer día del período solicitado</span>
        </div>

        <i className="bi bi-arrow-right shrink-0 pb-8 text-muted max-sm:hidden" aria-hidden="true" />

        <div className="min-w-40 flex-1">
          <label className="mb-1 flex items-center gap-1.5 text-2 font-semibold text-ink-2"
                 htmlFor={`${id}-fin`}>
            <span aria-hidden="true"
                  className="inline-grid size-4 place-items-center rounded-full bg-sunken text-1 font-bold">2</span>
            Fecha de finalización
          </label>
          <input
            type="date" id={`${id}-fin`} value={fin}
            min={inicio || minInicio}
            aria-describedby={`${id}-info`}
            className={clasesDeControl}
            onChange={(e) => poner('fin', e.target.value)}
          />
          <span className="mt-1 block text-1 text-muted">Último día del período solicitado</span>
        </div>
      </div>

      <div
        id={`${id}-info`}
        aria-live="polite"
        className={cn(
          'mt-1 flex items-start gap-2 rounded-sm border px-3 py-2 text-2 leading-relaxed',
          estado.estado === 'ok' ? 'border-ok/40 bg-ok/10 text-ok-ink'
            : estado.estado === 'error' ? 'border-danger/40 bg-danger/10 text-danger-ink'
            : 'border-line bg-sunken text-muted',
        )}
      >
        <i className={cn('bi', icono, 'mt-0.5 shrink-0')} aria-hidden="true" />
        <span>
          <span className="block font-semibold">{estado.titulo}</span>
          {estado.detalle && <span className="mt-0.5 block opacity-90">{estado.detalle}</span>}
        </span>
      </div>

      {regla && (
        <p className="flex items-start gap-1.5 text-1 text-muted">
          <i className="bi bi-info-circle mt-0.5 shrink-0" aria-hidden="true" />{regla}
        </p>
      )}

      {error && <p className="text-2 text-danger-ink" role="alert">{error}</p>}
    </div>
  );
}
